import { useMemo, Activity, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';

import {
  IconEye,
  IconPlus,
  IconTrash,
  IconSearch,
  IconNotebook,
  IconAdjustments,
  IconAlertCircle,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomImage from '@/components/CustomImage';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import TeacherLessonDetailsSkeleton from '@/components/SkeletonLoader/TeacherLessonDetailsSkeleton';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { ELessonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getTeacherLessonDetails,
  getTeacherLessonStudents,
  getTeacherLessonBreakdown,
  removeTeacherLessonBreakdownGame,
} from '@/service/lessonService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import {
  ILessonStudent,
  ILessonDetailsData,
  ILessonBreakdownItem,
} from '@/types/lesson';
import { getAssetUrl, formatLastActive, formatUnixToLongDate } from '@/utils';

import LessonBreakdownListSkeleton from '../Classes/components/LessonBreakdownListSkeleton';
import DetailsFilterModal, {
  ILessonDetailsFilters,
} from './components/DetailsFilterModal';
import StudentDetailsModal from './components/StudentDetailsModal';

const TeacherLessonDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { formatText } = useFormatText();
  const navigate = useNavigate();
  const { setIsBreadcrumb } = usePageTitle();
  const { isTableLoading, setTableLoading, isScreenLoading, setScreenLoading } =
    useSkeletonStore();

  const [lessonDetails, setLessonDetails] = useState<ILessonDetailsData | null>(
    null
  );
  const [students, setStudents] = useState<ILessonStudent[]>([]);
  const [totalStudents, setTotalStudents] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<ILessonDetailsFilters | null>(null);
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null
  );
  const [lessonBreakdown, setLessonBreakdown] = useState<{
    total_games: number;
    lesson_breakdown: ILessonBreakdownItem[];
  }>({
    total_games: 0,
    lesson_breakdown: [],
  });

  const [isBreakdownLoading, setIsBreakdownLoading] = useState(false);
  const [breakdownDeleteModal, setBreakdownDeleteModal] = useState<{
    open: boolean;
    game: ILessonBreakdownItem | null;
  }>({ open: false, game: null });

  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >('ASC');

  const { register, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  const pageTitle = formatText('sidebar.lessons');
  const lessonTitle = lessonDetails?.title || '...';

  useEffect(() => {
    setIsBreadcrumb([
      { label: pageTitle, to: '/teacher/lessons' },
      { label: lessonTitle },
    ]);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonTitle, pageTitle]);
  useEffect(() => {
    if (id) {
      fetchLessonDetails();
      fetchLessonBreakdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentPage, debouncedSearch, sortField, sortDirection, filters]);

  const fetchLessonDetails = async () => {
    if (!id) return;
    setScreenLoading(true);
    try {
      const response = await getTeacherLessonDetails(id);
      if (response?.status) {
        setLessonDetails(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setScreenLoading(false);
    }
  };

  const fetchLessonBreakdown = async () => {
    if (!id) return;
    setIsBreakdownLoading(true);
    try {
      const response = await getTeacherLessonBreakdown(id);
      if (response?.status) {
        setLessonBreakdown(
          response.data ?? { total_games: 0, lesson_breakdown: [] }
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsBreakdownLoading(false);
    }
  };

  const handleRemoveBreakdownGame = async () => {
    if (!id || !breakdownDeleteModal.game) return;
    try {
      const response = await removeTeacherLessonBreakdownGame(
        id,
        breakdownDeleteModal.game.id
      );
      if (response?.status) {
        setBreakdownDeleteModal({ open: false, game: null });
        fetchLessonBreakdown();
        fetchLessonDetails();
        fetchStudents();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchStudents = async () => {
    if (!id) return;
    setTableLoading(true);
    try {
      const pageSize = 10;
      const query = {
        search: debouncedSearch || undefined,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        order: sortField ? { [sortField]: sortDirection } : undefined,
        status: filters?.status || undefined,
        student_ids:
          Array.isArray(filters?.studentName) && filters?.studentName.length > 0
            ? filters?.studentName
            : undefined,
      };

      const response = await getTeacherLessonStudents(id, query);
      if (response?.status) {
        setStudents(response.data || []);
        setTotalStudents(response.total || 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      setIsBreadcrumb([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: 'teacher-details.student-table.columns.student-name',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (record: ILessonStudent) => (
          <div className="flex items-center gap-2">
            <UserAvatarList
              user={record.name}
              image={getAssetUrl(record?.profile_pic)}
              className="text-xs!"
            />
            <span className="text-neutral-primary font-medium text-sm">
              {record.name}
            </span>
          </div>
        ),
      },

      {
        key: 'progress',
        title: 'lessons.details-drawer.progress',
        sorter: true,
        handleSort: () => handleSort('progress'),
        render: (record: ILessonStudent) => (
          <div className="w-full max-w-30">
            <ProgressBar value={record.progress ?? 0} />
          </div>
        ),
      },
      {
        key: 'accuracy',
        title: 'lessons.details-drawer.accuracy',
        sorter: true,
        handleSort: () => handleSort('accuracy'),
        render: (record: ILessonStudent) => (
          <span className="text-neutral-secondary text-sm">
            {record.accuracy ?? 0}%
          </span>
        ),
      },
      {
        key: 'status',
        title: 'lessons.columns.status',
        sorter: true,
        handleSort: () => handleSort('status'),
        render: (record: ILessonStudent) => {
          const config = record?.status
            ? LessonStatusConfig[record?.status as ELessonStatus]
            : null;
          return (
            <TagCapsule
              value={config?.label ? formatText(config.label) : ''}
              className={config?.className}
            />
          );
        },
      },
      {
        key: 'last_active',
        title: 'lessons.columns.last-active',
        sorter: true,
        handleSort: () => handleSort('last_active'),
        render: (record: ILessonStudent) => (
          <span className="text-neutral-secondary text-sm">
            {formatLastActive(Number(record?.last_active), formatText)}
          </span>
        ),
      },
      {
        key: 'action',
        title: 'lessons.columns.action',
        align: 'center' as const,
        render: (record: ILessonStudent) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'common.actions.view',
                icon: <IconEye size={18} />,
                onClick: () => {
                  handleOpenStudentModal(record.id);
                },
              },
            ]}
          />
        ),
      },
    ],
    [formatText] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleOpenStudentModal = (studentId: string) => {
    setSelectedStudentId(studentId);
    setIsStudentModalOpen(true);
  };

  const renderBreakdownContent = () => {
    if (isBreakdownLoading) {
      return <LessonBreakdownListSkeleton />;
    }

    if (lessonBreakdown?.lesson_breakdown?.length > 0) {
      return (
        <div className="flex flex-col gap-4 flex-1 overflow-y-auto min-h-0">
          {lessonBreakdown?.lesson_breakdown.map(
            (item: ILessonBreakdownItem) => {
              const config = item?.status
                ? LessonStatusConfig[item.status as ELessonStatus]
                : null;
              return (
                <div
                  key={item?.id}
                  className="flex items-center justify-between pb-4 border-b border-neutral-100 last:border-0 last:pb-0"
                >
                  <div className="flex items-center gap-2 max-w-[60%]">
                    <span
                      className="font-semibold text-neutral-primary truncate"
                      title={item?.title}
                    >
                      {item?.title}
                    </span>
                    {item?.quarters !== null &&
                      item?.quarters !== undefined && (
                        <TagCapsule
                          value={`Q${item.quarters}`}
                          className="bg-purple-50 text-brand-default"
                        />
                      )}
                    {item?.rounds !== null && item?.rounds !== undefined && (
                      <TagCapsule
                        value={`R${item.rounds}`}
                        className="bg-neutral-100 text-neutral-primary"
                      />
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <TagCapsule
                      value={config?.label ? formatText(config.label) : ''}
                      className={config?.className}
                    />
                    <IconTrash
                      size={20}
                      className="text-red-500 cursor-pointer"
                      onClick={() =>
                        setBreakdownDeleteModal({
                          open: true,
                          game: item,
                        })
                      }
                    />
                  </div>
                </div>
              );
            }
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center grow py-10 gap-3">
        <CustomImage
          src={'/not-found.svg'}
          alt="noData"
          width={100}
          height={100}
        />
        <p className="text-neutral-900 font-semibold">
          {formatText('common.no-data-found')}
        </p>
      </div>
    );
  };

  const isFilterApplied = !!filters?.status || !!filters?.studentName?.length;

  const isEmpty =
    !isScreenLoading &&
    !isTableLoading &&
    students.length === 0 &&
    !debouncedSearch &&
    !isFilterApplied;

  const isEmptySearch =
    !isScreenLoading &&
    !isTableLoading &&
    students.length === 0 &&
    (!!debouncedSearch || isFilterApplied);

  if (isScreenLoading) {
    return <TeacherLessonDetailsSkeleton columns={columns} />;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="lesson-details-page">
      <div className="flex items-center gap-4 flex-1 ps-1">
        <Input
          name="search"
          register={register}
          placeholder={formatText(
            'lessons.teacher-lesson-details-search-placeholder'
          )}
          leftIcon={{
            icon: <IconSearch size={20} className="text-neutral-500" />,
          }}
          className="max-w-70"
          inputClassName="py-[13px]"
        />
        <div className="relative">
          <Button
            variant="outline"
            label={formatText('common.filter')}
            icon={<IconAdjustments size={20} />}
            className={`px-4 h-12 ${
              isFilterApplied ? 'border-brand-default! text-brand-default!' : ''
            }`}
            onClick={() => setIsFilterModalOpen(true)}
          />
          {isFilterApplied && (
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
          )}
        </div>

        <div className="ms-auto">
          <Button
            variant="primary"
            label={formatText('create-lesson.add-game')}
            icon={<IconPlus size={20} />}
            className="h-12"
            onClick={() =>
              navigate(`/teacher/lessons/${id}/add-game`, {
                state: { lessonId: id, lessonTitle },
              })
            }
          />
        </div>
      </div>

      <div
        className="flex flex-col gap-4"
        data-testid="lesson-overview-section"
      >
        <h2 className="text-lg font-semibold text-neutral-primary">
          {formatText('lessons.lesson-overview')}
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="w-full p-6 bg-orange-100 rounded-2xl flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="p-2 bg-orange-500 rounded-lg flex items-center justify-center">
                  <IconNotebook size={16} className="text-white" />
                </span>
                <h2 className="text-lg font-semibold text-neutral-primary">
                  {lessonDetails?.title || '-'}
                </h2>
              </div>

              {lessonDetails?.description && (
                <p className="text-neutral-secondary text-sm">
                  {lessonDetails.description}
                </p>
              )}

              <div className="flex gap-10">
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-neutral-secondary">
                    {formatText('lessons.assigned-date')}
                  </span>
                  <span className="font-semibold text-neutral-primary">
                    {lessonDetails?.assign_date
                      ? formatUnixToLongDate(lessonDetails.assign_date)
                      : '-'}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-neutral-secondary">
                    {formatText('lessons.due-date')}
                  </span>
                  <span className="font-semibold text-neutral-primary">
                    {lessonDetails?.due_date
                      ? formatUnixToLongDate(lessonDetails.due_date)
                      : '-'}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex justify-between">
                  <span className="font-semibold text-neutral-primary">
                    {formatText('lessons.average-progress')}
                  </span>
                  <span className="font-semibold text-neutral-primary">
                    {lessonDetails?.average_progress}%
                  </span>
                </div>
                <ProgressBar
                  value={lessonDetails?.average_progress || 0}
                  showPercentage={false}
                />
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <SummaryCard
                  variant="highlight"
                  label={formatText('lessons.class-assigned')}
                  value={
                    lessonDetails?.class_assigned
                      ?.map((c) => c?.name)
                      .join(', ') || '-'
                  }
                  bg="bg-white"
                  sub="text-neutral-primary!"
                  text="text-2xl! text-neutral-primary!"
                />
                <SummaryCard
                  variant="highlight"
                  label={formatText('lessons.students')}
                  value={String(lessonDetails?.students_count || 0)}
                  bg="bg-white"
                  sub="text-neutral-primary!"
                  text="text-2xl! text-neutral-primary!"
                />
                <SummaryCard
                  variant="highlight"
                  label={formatText('lessons.lesson-breakdown')}
                  value={String(lessonDetails?.lesson_breakdown || 0)}
                  bg="bg-white"
                  sub="text-neutral-primary!"
                  text="text-2xl! text-neutral-primary!"
                />
              </div>
            </div>
            {/* Instructions */}
            {lessonDetails?.instructions && (
              <div className="rounded-2xl bg-yellow-50 p-6 space-y-2">
                <div className="flex items-center gap-2">
                  <IconAlertCircle
                    size={32}
                    className="text-white p-2 rounded-lg bg-yellow-500 shrink-0"
                  />
                  <h4 className="font-semibold text-neutral-primary">
                    {formatText('lessons.details-drawer.instructions')}
                  </h4>
                </div>
                <p className="text-sm text-neutral-secondary">
                  {lessonDetails.instructions}
                </p>
              </div>
            )}
          </div>

          {/* Lesson Breakdown Section */}
          <div className="relative lg:overflow-hidden max-h-96 lg:max-h-none">
            <div className="lg:absolute lg:inset-0 w-full p-6 rounded-2xl border border-neutral-200 flex flex-col gap-6 overflow-hidden">
              <div className="flex items-center gap-2 shrink-0">
                <h2 className="text-lg font-semibold text-neutral-primary">
                  {formatText('lessons.lesson-breakdown')}
                </h2>
                <span className="px-2 py-0.5 bg-neutral-100 text-neutral-primary rounded-full text-xs font-semibold">
                  {lessonBreakdown?.total_games?.toString().padStart(2, '0')}
                </span>
              </div>

              {renderBreakdownContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Students Table Section */}
      <div className="flex flex-col gap-6" data-testid="students-table-section">
        <CustomTable
          columns={columns}
          dataSource={students}
          total={totalStudents}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={10}
          fieldName={sortField}
          sortDirection={sortDirection}
          onRowClick={(id) => handleOpenStudentModal(id)}
          isEmpty={isEmpty}
          isEmptySearch={isEmptySearch}
          isLoading={isTableLoading}
          emptyClassName="max-h-[500px]"
        />
      </div>

      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <DetailsFilterModal
          isOpen={isFilterModalOpen}
          setIsOpen={setIsFilterModalOpen}
          initialFilters={filters}
          onApplyFilters={(newFilters) => {
            setFilters(newFilters);
            setCurrentPage(1);
          }}
        />
      </Activity>

      <Activity mode={isStudentModalOpen ? 'visible' : 'hidden'}>
        <StudentDetailsModal
          isOpen={isStudentModalOpen}
          onClose={() => {
            setIsStudentModalOpen(false);
            setSelectedStudentId(null);
          }}
          studentId={selectedStudentId}
          lessonId={id || null}
        />
      </Activity>

      <Activity mode={breakdownDeleteModal.open ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={breakdownDeleteModal.open}
          setIsOpen={(open) =>
            setBreakdownDeleteModal({
              open,
              game: open ? breakdownDeleteModal.game : null,
            })
          }
          title={formatText('lessons.actions.remove-breakdown-title')}
          descriptionPrefix={formatText('common.confirmation.remove-prefix')}
          descriptionSuffix={formatText(
            'lessons.actions.remove-breakdown-suffix'
          )}
          entityName={breakdownDeleteModal.game?.title || ''}
          confirmLabel={formatText('lessons.actions.remove-breakdown')}
          cancelLabel={formatText('common.actions.cancel')}
          onConfirm={handleRemoveBreakdownGame}
          isDestructive
        />
      </Activity>
    </div>
  );
};

export default TeacherLessonDetails;
